import {
    HEATMAP_COLOR_MAPS,
    type HeatmapColorMap
} from 'app/modules/wallforming/components/content-container/heatmap/types/heatmapColorMap';
import {
    HEATMAP_METRICS,
    type HeatmapMetric
} from 'app/modules/wallforming/components/content-container/heatmap/types/heatmapMetric';
import { LengthUnit } from 'app/modules/wallforming/components/scenes/types/lengthUnit';

import type { GraphicsSettings } from 'app/modules/wallforming/components/setting-container/types/graphicSettings';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

interface CopilotSetting {
    readonly options: readonly string[];
    readonly read: (state: PlacementState) => string;
    readonly write: (state: PlacementState, value: string) => void;
}

const ON_OFF = ['on', 'off'] as const;

/** Whitelisted choice setting; the written value is always one of the predefined options. */
const choice = <T extends string>(
    options: readonly T[],
    read: (state: PlacementState) => T,
    write: (state: PlacementState, value: T) => void
): CopilotSetting => ({
    options,
    read,
    write: (state, value) => {
        const option = options.find((candidate) => candidate === value);
        if (option === undefined) {
            throw new Error(`Unsupported setting value ${value}.`);
        }
        write(state, option);
    }
});

const toggle = (read: (state: PlacementState) => boolean, write: (state: PlacementState, on: boolean) => void) =>
    choice(
        ON_OFF,
        (state) => (read(state) ? 'on' : 'off'),
        (state, value) => write(state, value === 'on')
    );

type GraphicsToggle = { [K in keyof GraphicsSettings]: GraphicsSettings[K] extends boolean ? K : never }[keyof GraphicsSettings];
const graphicsToggle = (key: GraphicsToggle) =>
    toggle(
        (state) => state.graphicsSettings[key],
        (state, on) => state.setGraphicsSettings({ [key]: on })
    );

/** Single source of truth for settings Copilot may change; each entry exposes only predefined values. */
export const copilotSettings = {
    productStrategy: toggle(
        (state) => state.isPPMFilterActive,
        (state, on) => state.setIsPPMFilterActive(on)
    ),
    lengthUnit: choice(
        Object.values(LengthUnit),
        (state) => state.lengthUnit,
        (state, unit) => state.setLengthUnit(unit)
    ),
    heatmap: toggle(
        (state) => state.showHeatmapOverlay,
        (state, on) => state.setShowHeatmapOverlay(on)
    ),
    heatmapLabels: toggle(
        (state) => state.showHeatmapLabels,
        (state, on) => state.setShowHeatmapLabels(on)
    ),
    heatmapHoverMetrics: toggle(
        (state) => state.showHeatmapHover,
        (state, on) => state.setShowHeatmapHover(on)
    ),
    heatmapMetric: choice<HeatmapMetric>(
        HEATMAP_METRICS,
        (state) => state.heatmapMetric,
        (state, metric) => state.setHeatmapMetric(metric)
    ),
    heatmapColorMap: choice<HeatmapColorMap>(
        HEATMAP_COLOR_MAPS,
        (state) => state.heatmapColorMap,
        (state, colorMap) => state.setHeatmapColorMap(colorMap)
    ),
    placementPreview: toggle(
        (state) => state.showPlacementPreview,
        (state, on) => state.setShowPlacementPreview(on)
    ),
    shadows: graphicsToggle('shadowsEnabled'),
    reflections: graphicsToggle('reflectionsEnabled'),
    edgesOverlay: graphicsToggle('edgesOverlayEnabled'),
    ifcModel: graphicsToggle('showIfc'),
    fps: graphicsToggle('showFps')
} satisfies Record<string, CopilotSetting>;

export type CopilotSettingId = keyof typeof copilotSettings;

export const copilotSettingIds = Object.keys(copilotSettings) as CopilotSettingId[];
export const copilotSettingValues = [...new Set(Object.values(copilotSettings).flatMap(({ options }) => options))];

export const isCopilotSettingId = (id: string): id is CopilotSettingId => id in copilotSettings;

export const applyCopilotSetting = (state: PlacementState, id: string, value: string) => {
    if (!isCopilotSettingId(id)) {
        throw new Error('Setting is not permitted.');
    }
    copilotSettings[id].write(state, value);
};

/** Current value and allowed options per setting, for the model's context. */
export const readCopilotSettings = (state: PlacementState) =>
    Object.fromEntries(
        copilotSettingIds.map((id) => [
            id,
            { value: copilotSettings[id].read(state), options: copilotSettings[id].options }
        ])
    );
